/**
 * El portal del empleado. Mobile-first: se usa de pie, en un pasillo, con una
 * mano. En escritorio la carcasa se vuelve rail + columnas (ver app.css,
 * «La carcasa móvil en escritorio») y esta hoja agrega lo propio del personal.
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { Brandmark } from '@/components/common/Brandmark';
import { Icon, type IconName } from '@/components/common/NavIcons';
import '@/styles/personal.css';

/* Íconos por nombre, no glifos: en el teléfono «☀» y «☺» se pintaban como
   emoji a todo color, ajenos a la familia del sistema. */
const TABS: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: '/empleado', label: 'Mi jornada', icon: 'inicio', end: true },
  { to: '/empleado/insumos', label: 'Insumos', icon: 'inventario' },
  { to: '/empleado/horas', label: 'Horas extra', icon: 'horas' },
  { to: '/empleado/vacaciones', label: 'Vacaciones', icon: 'vacaciones' },
  { to: '/empleado/perfil', label: 'Mi perfil', icon: 'perfil' },
];

/** El título de la pantalla, sacado de la misma lista que pinta la nav: una
 *  sola fuente para el nombre de cada destino. La visita no es pestaña. */
function tituloDe(pathname: string): string {
  if (pathname.startsWith('/empleado/visitas/')) return 'Visita';
  const tab = TABS.find((t) => (t.end ? pathname === t.to : pathname.startsWith(t.to)));
  return tab?.label ?? 'Mi jornada';
}

export function EmployeeLayout() {
  const { user, logout } = useSession();
  const { pathname } = useLocation();
  const titulo = tituloDe(pathname);

  return (
    <div className="mobile-shell">
      <header className="mhead">
        <div className="mhead__top">
          <Brandmark size={30} />
          <span>
            <span className="mhead__name">Clean Blue</span>
            <span className="mhead__sub" style={{ display: 'block' }}>{user?.full_name}</span>
          </span>
          <span className="spacer" />
          {/* En el teléfono «Salir» vive acá; en escritorio se esconde y lo
              reemplaza el pie del rail (abajo). Sin confirmación: salir es
              barato de deshacer, entrar de nuevo cuesta un toque. */}
          <button
            type="button"
            className="btn btn--ghost btn--sm mhead__salir"
            onClick={() => void logout()}
          >
            <Icon name="salir" size={14} />
            Salir
          </button>
        </div>
        {/* El nombre de la pantalla, como en el panel: en escritorio la
            pestaña activa del rail queda lejos del contenido y sin esto el
            cuerpo arrancaba directo en una tarjeta. */}
        <h1 className="mhead__title">{titulo}</h1>
      </header>

      <div className="mobile-shell__body">
        <Outlet />
      </div>

      <nav className="bottomnav" style={{ gridTemplateColumns: `repeat(${TABS.length}, 1fr)` }}>
        {/* Solo se ve en el rail de escritorio (`.railbrand` en app.css): en
            el teléfono la marca sigue viviendo en `.mhead__top`, arriba. */}
        <div className="railbrand">
          <div className="railbrand__logo">
            <Brandmark size={27} />
            <span className="railbrand__name">Clean Blue</span>
          </div>
          <span className="railbrand__org">{user?.full_name}</span>
        </div>
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end}
            className={({ isActive }) => (isActive ? 'is-active' : '')}>
            <span className="ico" aria-hidden="true"><Icon name={tab.icon} size={20} /></span>
            {tab.label}
          </NavLink>
        ))}
        <button type="button" className="bottomnav__pie" onClick={() => void logout()}>
          <span className="ico" aria-hidden="true"><Icon name="salir" size={20} /></span>
          Salir
        </button>
      </nav>
    </div>
  );
}
